
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from '../../lib/hooks/use-debounce';
import {
  useGlobalSearch,
  useRecentSearches,
  useSearchSuggestions,
  saveRecentSearch,
  clearRecentSearches,
  SEARCH_TYPE_LABELS,
  type SearchResultType,
  type SearchResult,
} from '../../lib/api/search';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '../ui/command';
import { Badge } from '../ui/badge';
import { MOD_SYMBOL } from '../../lib/config/keyboard-shortcuts';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import {
  Package,
  UserCircle,
  Building2,
  Truck,
  Container,
  FileText,
  DollarSign,
  File,
  Clock,
  TrendingUp,
  Search,
  X,
  ArrowRight,
} from 'lucide-react';

// Icon mapping for result types
const TypeIcons: Record<SearchResultType, React.ElementType> = {
  load: Package,
  driver: UserCircle,
  customer: Building2,
  carrier: Truck,
  truck: Truck,
  trailer: Container,
  invoice: FileText,
  settlement: DollarSign,
  document: File,
};

// Color mapping for result types
const TypeColors: Record<SearchResultType, string> = {
  load: 'bg-blue-100 text-blue-700',
  driver: 'bg-green-100 text-green-700',
  customer: 'bg-purple-100 text-purple-700',
  carrier: 'bg-orange-100 text-orange-700',
  truck: 'bg-cyan-100 text-cyan-700',
  trailer: 'bg-teal-100 text-teal-700',
  invoice: 'bg-amber-100 text-amber-700',
  settlement: 'bg-emerald-100 text-emerald-700',
  document: 'bg-gray-100 text-gray-700',
};

interface GlobalSearchProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function GlobalSearch({ open: controlledOpen, onOpenChange }: GlobalSearchProps) {
  const navigate = useNavigate();
  const [internalOpen, setInternalOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [selectedType, setSelectedType] = React.useState<SearchResultType | null>(null);
  const debouncedQuery = useDebounce(query, 300);

  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  // Queries
  const { data: searchResults, isLoading: isSearching } = useGlobalSearch({
    query: debouncedQuery,
    types: selectedType ? [selectedType] : undefined,
    limit: 20,
  });

  const { data: recentSearches } = useRecentSearches();
  const { data: suggestions } = useSearchSuggestions(debouncedQuery);

  // Keyboard shortcut
  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen(!isOpen);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [isOpen, setOpen]);

  // Reset state when closing
  React.useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setSelectedType(null);
    }
  }, [isOpen]);

  const handleSelect = async (result: SearchResult) => {
    await saveRecentSearch(query);
    setOpen(false);
    navigate(result.url);
  };

  const handleSuggestionSelect = (suggestionQuery: string) => {
    setQuery(suggestionQuery);
  };

  const handleClearRecent = async () => {
    await clearRecentSearches();
  };

  const handleTypeFilter = (type: SearchResultType | null) => {
    setSelectedType(type === selectedType ? null : type);
  };

  const showResults = debouncedQuery.length >= 2;
  const showRecent = !showResults && recentSearches && recentSearches.length > 0;
  const showSuggestions = query.length >= 1 && suggestions && suggestions.length > 0 && !showResults;

  return (
    <CommandDialog open={isOpen} onOpenChange={setOpen}>
      <CommandInput
        placeholder="Search loads, drivers, customers..."
        value={query}
        onValueChange={setQuery}
      />

      {/* Type filters */}
      {showResults && searchResults && searchResults.categories.length > 0 && (
        <div className="flex flex-wrap gap-1 px-3 py-2 border-b">
          <Badge
            variant={selectedType === null ? 'default' : 'outline'}
            className="cursor-pointer"
            onClick={() => handleTypeFilter(null)}
          >
            All ({searchResults.totalResults})
          </Badge>
          {searchResults.categories.map(cat => (
            <Badge
              key={cat.type}
              variant={selectedType === cat.type ? 'default' : 'outline'}
              className={`cursor-pointer ${selectedType === cat.type ? '' : TypeColors[cat.type]}`}
              onClick={() => handleTypeFilter(cat.type)}
            >
              {cat.label} ({cat.count})
            </Badge>
          ))}
        </div>
      )}

      <CommandList>
        {/* Loading state */}
        {isSearching && showResults && (
          <div className="p-4 space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        )}

        {/* No results */}
        {!isSearching && showResults && searchResults?.results.length === 0 && (
          <CommandEmpty>
            <div className="text-center py-6">
              <Search className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                No results found for &ldquo;{debouncedQuery}&rdquo;
              </p>
              {searchResults?.suggestions && searchResults.suggestions.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs text-muted-foreground mb-2">Suggestions:</p>
                  <div className="flex flex-wrap gap-1 justify-center">
                    {searchResults.suggestions.map((s, i) => (
                      <Badge
                        key={i}
                        variant="outline"
                        className="cursor-pointer"
                        onClick={() => setQuery(s.replace('Try searching for ', '').replace(/"/g, ''))}
                      >
                        {s}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CommandEmpty>
        )}

        {/* Search results */}
        {!isSearching && showResults && searchResults && searchResults.results.length > 0 && (
          <CommandGroup heading={`Results (${searchResults.totalResults}) - ${searchResults.searchTime}ms`}>
            {searchResults.results.map(result => {
              const Icon = TypeIcons[result.type];
              return (
                <CommandItem
                  key={`${result.type}-${result.id}`}
                  value={`${result.type}-${result.id}`}
                  onSelect={() => handleSelect(result)}
                  className="flex items-center gap-3 py-3"
                >
                  <div className={`p-1.5 rounded ${TypeColors[result.type]}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{result.title}</span>
                      <Badge variant="outline" className="text-[10px] px-1">
                        {SEARCH_TYPE_LABELS[result.type]}
                      </Badge>
                    </div>
                    {result.subtitle && (
                      <p className="text-sm text-muted-foreground truncate">{result.subtitle}</p>
                    )}
                    {result.description && (
                      <p className="text-xs text-muted-foreground truncate">{result.description}</p>
                    )}
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        {/* Suggestions */}
        {showSuggestions && (
          <CommandGroup heading="Suggestions">
            {suggestions?.map((suggestion, index) => (
              <CommandItem
                key={`suggestion-${index}`}
                value={`suggestion-${suggestion.query}`}
                onSelect={() => handleSuggestionSelect(suggestion.query)}
                className="flex items-center gap-2"
              >
                {suggestion.type === 'recent' ? (
                  <Clock className="h-4 w-4 text-muted-foreground" />
                ) : suggestion.type === 'popular' ? (
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Search className="h-4 w-4 text-muted-foreground" />
                )}
                <span>{suggestion.query}</span>
                {suggestion.category && (
                  <Badge variant="outline" className="text-[10px] ml-auto">
                    {SEARCH_TYPE_LABELS[suggestion.category]}
                  </Badge>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {/* Recent searches */}
        {showRecent && (
          <>
            <CommandGroup heading="Recent Searches">
              {recentSearches?.slice(0, 5).map(recent => (
                <CommandItem
                  key={recent.id}
                  value={`recent-${recent.id}`}
                  onSelect={() => setQuery(recent.query)}
                  className="flex items-center gap-2"
                >
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span>{recent.query}</span>
                  <span className="text-xs text-muted-foreground ml-auto">
                    {recent.resultCount} results
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
            <div className="p-2">
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-muted-foreground"
                onClick={handleClearRecent}
              >
                <X className="h-3 w-3 mr-1" />
                Clear recent searches
              </Button>
            </div>
          </>
        )}

        {/* Quick actions when empty */}
        {!query && !showRecent && (
          <CommandGroup heading="Quick Actions">
            <CommandItem onSelect={() => { setOpen(false); navigate('/loads/new'); }}>
              <Package className="mr-2 h-4 w-4" />
              Create new load
            </CommandItem>
            <CommandItem onSelect={() => { setOpen(false); navigate('/drivers'); }}>
              <UserCircle className="mr-2 h-4 w-4" />
              View all drivers
            </CommandItem>
            <CommandItem onSelect={() => { setOpen(false); navigate('/customers'); }}>
              <Building2 className="mr-2 h-4 w-4" />
              View all customers
            </CommandItem>
            <CommandItem onSelect={() => { setOpen(false); navigate('/invoices'); }}>
              <FileText className="mr-2 h-4 w-4" />
              View invoices
            </CommandItem>
          </CommandGroup>
        )}
      </CommandList>

      <div className="border-t px-3 py-2 text-xs text-muted-foreground flex items-center justify-between">
        <div className="flex items-center gap-2">
          <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px]">↑↓</kbd>
          <span>Navigate</span>
          <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px]">↵</kbd>
          <span>Select</span>
          <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px]">Esc</kbd>
          <span>Close</span>
        </div>
        <div>
          <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px]">{MOD_SYMBOL}</kbd>
          <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] ml-0.5">K</kbd>
        </div>
      </div>
    </CommandDialog>
  );
}

// Hook to control global search from anywhere
export function useGlobalSearchOpen() {
  const [open, setOpen] = React.useState(false);
  return { open, setOpen, toggle: () => setOpen(o => !o) };
}
